import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Skeleton, Stack } from "@mui/material";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { CrisisPlanFormValues } from "./interface";
import useGetCrisisPlan from "hooks/api/client/useGetCrisisPlan";
import useUpsertCrisisPlan from "hooks/api/client/useUpsertCrisisPlan";
import { useGetClientById } from "hooks/api/useGetClientById";
import { SAFETY_CONCERNS_OPTIONS } from "../StrengthsAndAreasOfNeed/AreasOfConcern/interface";
import { SelectOption } from "../../Widgets/LabeledSelect";

const EMPTY_VALUES: CrisisPlanFormValues = {
  hasCrisisPlan: "",
  crisisPlan: "",
  behaviorsIdentified: [],
  additionalConsiderations: "",
};

export const CrisisPlan: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const {
    crisisPlan,
    refetchCrisisPlan,
    isFetched: isCrisisPlanFetched,
  } = useGetCrisisPlan(clientIdNum);
  const { data: clientData } = useGetClientById(clientIdNum);

  const { upsertCrisisPlanMutation, isUpsertCrisisPlanLoading } = useUpsertCrisisPlan({
    onSuccess: () => {
      refetchCrisisPlan();
      setEditMode(false);
    },
  });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<CrisisPlanFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<CrisisPlanFormValues>(EMPTY_VALUES);

  const behaviorsOptions: SelectOption[] = React.useMemo(() => {
    const safetyConcerns: string[] = clientData?.data?.safetyConcerns ?? [];
    return SAFETY_CONCERNS_OPTIONS.filter((o) => safetyConcerns.includes(o.value));
  }, [clientData?.data?.safetyConcerns]);

  useEffect(() => {
    if (editMode) return;
    if (crisisPlan) {
      const safetyConcerns = clientData?.data?.safetyConcerns;
      let behaviors: string[] = [];
      if (Array.isArray(crisisPlan.crisis_plan_behaviors)) {
        behaviors = crisisPlan.crisis_plan_behaviors.map((b: any) => b.behavior_identified);
      } else if (Array.isArray(crisisPlan.behaviorsIdentified)) {
        behaviors = crisisPlan.behaviorsIdentified;
      }
      if (Array.isArray(safetyConcerns)) {
        behaviors = behaviors.filter((v) => safetyConcerns.includes(v));
      }

      let hasCrisisPlan = "";
      if (crisisPlan.has_crisis_plan === true) {
        hasCrisisPlan = "yes";
      } else if (crisisPlan.has_crisis_plan === false) {
        hasCrisisPlan = "no";
      }

      const values: CrisisPlanFormValues = {
        hasCrisisPlan,
        crisisPlan: crisisPlan.crisis_plan || crisisPlan.crisisPlan || "",
        behaviorsIdentified: behaviors,
        additionalConsiderations:
          crisisPlan.additional_considerations || crisisPlan.additionalConsiderations || "",
      };
      setFormValues(values);
      setInitialValues(values);
    }
  }, [crisisPlan, clientData?.data, editMode]);

  useEffect(() => {
    if (!editMode) return;
    const safetyConcerns = clientData?.data?.safetyConcerns;
    if (!Array.isArray(safetyConcerns)) return;
    setFormValues((prev) => ({
      ...prev,
      behaviorsIdentified: prev.behaviorsIdentified.filter((v) => safetyConcerns.includes(v)),
    }));
  }, [clientData?.data?.safetyConcerns, editMode]);

  const handleChange =
    (field: keyof CrisisPlanFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setFormValues((prev) => {
        const updated = { ...prev, [field]: value };
        if (field === "hasCrisisPlan" && value === "no") {
          updated.crisisPlan = "";
          updated.behaviorsIdentified = [];
        }
        return updated;
      });
    };

  const handleBehaviorsChange = (value: string[]) => {
    setFormValues((prev) => ({ ...prev, behaviorsIdentified: value }));
  };

  const handleSave = () => {
    if (!clientIdNum) return;

    const behaviors = Array.isArray(formValues.behaviorsIdentified)
      ? formValues.behaviorsIdentified
      : [];
    const hasCrisisPlan =
      formValues.hasCrisisPlan === "yes" ? true : formValues.hasCrisisPlan === "no" ? false : null;

    const payload = {
      hasCrisisPlan,
      crisisPlan: formValues.crisisPlan || null,
      behaviorsIdentified: behaviors,
      additionalConsiderations: formValues.additionalConsiderations || null,
    };

    upsertCrisisPlanMutation({
      clientId: clientIdNum,
      payload,
      isNew: isCrisisPlanFetched && !crisisPlan,
    });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) || isUpsertCrisisPlanLoading;

  if (!isCrisisPlanFetched) {
    return (
      <SectionWrapper
        title="Crisis Plan"
        description="Necessity of crisis plan based on high-risk behaviors and documentation of safety considerations for telehealth interventions"
        hideButtons
      >
        <Stack gap={2}>
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="text" width="50%" height={28} />
          <Skeleton variant="rounded" height={96} />
        </Stack>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      title="Crisis Plan"
      description="Necessity of crisis plan based on high-risk behaviors and documentation of safety considerations for telehealth interventions"
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields
          formValues={formValues}
          handleChange={handleChange}
          handleBehaviorsChange={handleBehaviorsChange}
          behaviorsOptions={behaviorsOptions}
          hasSafetyConcerns={(clientData?.data?.safetyConcerns ?? []).length > 0}
        />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default CrisisPlan;
